import {
  Button,
  Flex,
  Select,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

interface PaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
}

export default function TablePagination({
  page,
  totalPages,
  onPageChange,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50],
}: PaginationProps) {
  const { t } = useTranslation();
  const textColor = useColorModeValue('secondaryGray.600', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  return (
    <Flex
      justify="space-between"
      align="center"
      px="20px"
      py="12px"
      borderTop="1px solid"
      borderColor={borderColor}
    >
      <Flex align="center" gap="8px">
        {onPageSizeChange && (
          <>
            <Text fontSize="sm" color={textColor}>
              {t('common.rowsPerPage', 'Rows per page')}:
            </Text>
            <Select
              size="sm"
              w="70px"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              borderRadius="8px"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
          </>
        )}
      </Flex>
      <Flex align="center" gap="12px">
        <Text fontSize="sm" color={textColor}>
          {t('common.pageOf', 'Page {{page}} of {{total}}', {
            page: page + 1,
            total: Math.max(totalPages, 1),
          })}
        </Text>
        <Button
          size="sm"
          variant="light"
          onClick={() => onPageChange(page - 1)}
          isDisabled={page === 0}
        >
          {t('common.previous', 'Previous')}
        </Button>
        <Button
          size="sm"
          variant="light"
          onClick={() => onPageChange(page + 1)}
          isDisabled={page >= totalPages - 1}
        >
          {t('common.next', 'Next')}
        </Button>
      </Flex>
    </Flex>
  );
}
